import { formatMoney, parseMoney } from '@ifixit/helpers';
import { LocaleCode } from '@ifixit/i18n';
import { ProductSearchResultItem } from '../types';

function getHitPriceTier(
   hit: ProductSearchResultItem,
   priceTier: string | null
): { default_variant_price: number; min: number; max: number } | null {
   if (!hit.price_tiers || !priceTier || !hit.price_tiers[priceTier]) {
      return null;
   }
   return hit.price_tiers[priceTier];
}

function getHitGroupPricing(hit: ProductSearchResultItem): { min: number; max: number } | null {
   if (hit.group_min_price === undefined || hit.group_max_price === undefined) {
      return null;
   }
   return {
      min: convertCentsToDecimal(hit.group_min_price),
      max: convertCentsToDecimal(hit.group_max_price),
   };
}

function convertCentsToDecimal(cents: number): number {
   return cents / 100;
}

function formatPriceRange(
   minPrice: number,
   maxPrice: number,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): { formattedPrice: string; isRange: boolean } {
   if (minPrice === maxPrice) {
      return {
         formattedPrice: formatMoney(parseMoney(minPrice, currency), localeCodeOrUrlPrefix),
         isRange: false,
      };
   }
   if (minPrice === 0) {
      return {
         formattedPrice: formatMoney(parseMoney(maxPrice, currency), localeCodeOrUrlPrefix),
         isRange: false,
      };
   }
   const formattedMin = formatMoney(parseMoney(minPrice, currency), localeCodeOrUrlPrefix);
   const formattedMax = formatMoney(parseMoney(maxPrice, currency), localeCodeOrUrlPrefix);
   return {
      formattedPrice: `${formattedMin} - ${formattedMax}`,
      isRange: true,
   };
}
type GetPriceResultProps = {
   price_float: number;
   compare_at_price: number | undefined;
   currency: string;
   localeCodeOrUrlPrefix: LocaleCode | string;
   price_range?: {
      groupPricing: { min: number; max: number };
      tierPricing?: { default_variant_price: number; min: number; max: number };
   };
};

type PriceResult = {
   formattedPrice: string;
   isDiscounted: boolean;
   formattedOriginalPrice: string;
   isRange: boolean;
   discountPercent: number;
};

function getPriceResult({
   price_float,
   price_range,
   compare_at_price,
   currency,
   localeCodeOrUrlPrefix,
}: GetPriceResultProps): PriceResult {
   const fullPrice = compare_at_price ?? price_float;

   const salePrice = Math.min(
      price_range?.tierPricing?.default_variant_price ?? Number.POSITIVE_INFINITY,
      price_float
   );

   const discounted = isDiscounted(fullPrice, salePrice);
   const discountPercent = getDiscountPercent(fullPrice, salePrice);

   if (price_range) {
      const { tierPricing, groupPricing } = price_range;
      const preferredRange = tierPricing || groupPricing;
      const { formattedPrice: formattedGroupPrice } = formatPriceRange(
         groupPricing.min,
         groupPricing.max,
         currency,
         localeCodeOrUrlPrefix
      );

      const { formattedPrice, isRange } = formatPriceRange(
         preferredRange.min,
         preferredRange.max,
         currency,
         localeCodeOrUrlPrefix
      );

      return {
         formattedPrice,
         formattedOriginalPrice: formattedGroupPrice,
         isDiscounted: discounted,
         isRange,
         discountPercent,
      };
   }

   return {
      formattedPrice: formatMoney(parseMoney(price_float, currency), localeCodeOrUrlPrefix),
      formattedOriginalPrice: formatMoney(parseMoney(fullPrice, currency), localeCodeOrUrlPrefix),
      isDiscounted: discounted,
      isRange: false,
      discountPercent,
   };
}

function isDiscounted(fullPrice: number, salePrice: number): boolean {
   return fullPrice > salePrice;
}

function getDiscountPercent(fullPrice: number, salePrice: number): number {
   return Math.round(((fullPrice - salePrice) / fullPrice) * 100);
}

export function determineProductPrice(
   hit: ProductSearchResultItem,
   priceTier: string | null,
   currency: string,
   localeCodeOrUrlPrefix: LocaleCode | string
): PriceResult {
   const tierPricing = getHitPriceTier(hit, priceTier);
   const groupPricing = getHitGroupPricing(hit);
   const sharedProps = {
      price_float: hit.price_float!,
      compare_at_price: hit.compare_at_price,
      currency,
      localeCodeOrUrlPrefix,
   };

   if (tierPricing && groupPricing) {
      return getPriceResult({
         ...sharedProps,
         price_range: { tierPricing, groupPricing },
      });
   }

   if (groupPricing) {
      return getPriceResult({
         ...sharedProps,
         price_range: { groupPricing },
      });
   }

   return getPriceResult(sharedProps);
}
